import React from 'react';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import * as Sift from '@sift/types';

// Components
import { SortableElement } from 'react-sortable-hoc';
import IconButton from '@material-ui/core/IconButton';
import RemoveCircleIcon from 'components/Icons/ProfileLayout/RemoveCircleIcon';
import SortableHandleElem from './SortableHandleElem';
import ArrowUp from '@material-ui/icons/ArrowUpward';
import ArrowDown from '@material-ui/icons/ArrowDownward';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

interface LayoutItem {
  type: 'field' | 'category';
  name: string;
}

interface LayoutValue {
  oldIndex: number;
  newIndex: number;
}

interface SortableItemElemProps {
  value: LayoutItem;
  fields: Sift.Field[];
  layout: LayoutItem[];
  itemIndex: number;
  onRemoveItem?: () => void;
  onLayoutChange: (value: LayoutValue) => void;
}

const SortableItemElem = SortableElement(
  ({ value, itemIndex, layout, fields, onLayoutChange, onRemoveItem }: SortableItemElemProps) => {
    const field = fields.find((field: Sift.Field) => field.objectKey === value.name);
    const classes = useStyles();

    const circleColorClass =
      field && field.restricted ? classes.redCircleIcon : classes.greenCircleIcon;

    if (!field) {
      return null;
    }

    return (
      <div className={classes.root}>
        <SortableHandleElem className={classes.dragIcon} />
        <div className={classes.itemName} data-testid={`${field.objectKey}-sortableItem`}>
          {field.displayName}
        </div>

        <div className={classes.rightSide}>
          <div className={classes.accessInfo}>
            <FiberManualRecordIcon className={classNames(classes.circleIcon, circleColorClass)} />
            {field && field.restricted ? 'Restricted' : 'Public'}
          </div>

          <div className={classes.arrowsContainer}>
            {itemIndex !== 0 && (
              <IconButton
                className={classes.buttonIcon}
                onClick={() => onLayoutChange({ oldIndex: itemIndex, newIndex: itemIndex - 1 })}
              >
                <ArrowUp className={classes.icon} />
              </IconButton>
            )}

            {itemIndex !== layout.length - 1 && (
              <IconButton
                className={classes.buttonIcon}
                onClick={() => onLayoutChange({ oldIndex: itemIndex, newIndex: itemIndex + 1 })}
              >
                <ArrowDown className={classes.icon} />
              </IconButton>
            )}
          </div>

          {!!onRemoveItem && (
            <IconButton onClick={onRemoveItem} className={classes.buttonIcon}>
              <RemoveCircleIcon className={classes.icon} />
            </IconButton>
          )}
        </div>
      </div>
    );
  },
);

const useStyles = makeStyles((theme: any) => {
  return {
    root: {
      display: 'flex',
      alignItems: 'center',
      zIndex: 2000,
      paddingLeft: theme.spacing(),
      paddingRight: theme.spacing(),
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      backgroundColor: theme.colors.grayBlue,
      borderColor: `1px solid ${theme.palette.grey[500]}`,
      marginBottom: theme.spacing(),
      '&:hover': {
        backgroundColor: theme.palette.grey[100],
      },
    },
    itemName: {
      flex: 1,
      ...theme.typography.body1,
    },
    buttonIcon: {
      marginLeft: theme.spacing(0.5),
      padding: theme.spacing(0.75),
    },
    dragIcon: {
      fontSize: 16,
      cursor: 'move',
      marginRight: theme.spacing(),
    },
    icon: {
      fontSize: 16,
    },
    rightSide: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      minWidth: 180,
    },
    circleIcon: {
      maxWidth: 8,
      maxHeight: 8,
      marginRight: theme.spacing(),
    },
    greenCircleIcon: {
      color: theme.colors.success,
    },
    redCircleIcon: {
      color: theme.colors.error,
    },
    accessInfo: {
      marginRight: 'auto',
    },
    arrowsContainer: {
      minWidth: 64,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
  };
});

export default SortableItemElem;
